<template>
  <div class="h-full overflow-auto">
    <div
      class="min-h-full flex gap-4 flex-col justify-end max-w-2xl p-4 mx-auto relative"
    >
      <div class="h-16 w-full top-0 bg-transparent"></div>
      <template v-if="isRecent">
        <ConversationLoading v-if="loading && !messages?.length" />
        <ConversationError v-else-if="error" @retry="emit('retry')" />
        <template v-else>
          <V2MessageBubble
            v-for="(message, idx) in messages"
            :key="message.id"
            :sender="message.role"
            :message="message.content"
            :is-last="idx === messages.length - 1"
            :created="message.created"
            :is-widget="isWidget"
            @ready="scrollToEnd"
            @confirm="handleConfirmAction"
          />
        </template>
      </template>
      <template v-else>
        <V2MessageBubble
          v-for="(message, idx) in v2ResponseStore.messages"
          :key="message.role + idx"
          :sender="message.role"
          :message="message.content"
          :is-last="idx === v2ResponseStore.messages.length - 1"
          :created="message.created"
          :is-widget="isWidget"
          @ready="scrollToEnd"
          @confirm="handleConfirmAction"
        />
      </template>
      <div v-if="isProcessing" class="flex gap-1 items-end">
        <div
          class="h-2 w-2 rounded-full border border-surface-100 dark:border-surface-700 bg-surface-0/40 dark:bg-surface-0/5 opacity-0 animate-[fade-in-up_300ms_ease-out_forwards]"
        ></div>
        <div
          class="h-4 w-4 rounded-full border border-surface-100 dark:border-surface-700 bg-surface-0/40 dark:bg-surface-0/5 opacity-0 animate-[fade-in-up_300ms_100ms_ease-out_forwards]"
        ></div>
        <div
          class="w-full py-1.5 px-3 rounded-full border border-surface-100 dark:border-surface-700 max-w-max bg-surface-0/40 dark:bg-surface-0/5 opacity-0 animate-[fade-in-up_300ms_200ms_ease-out_forwards]"
        >
          <transition
            enter-from-class="opacity-0 translate-y-2"
            enter-active-class="duration-300"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="duration-200"
            mode="out-in"
          >
            <p :key="currentLoadingTitle" class="shimmer font-medium">
              {{ currentLoadingTitle }}
            </p>
          </transition>
        </div>
      </div>
      <div ref="lastElement" class="h-2 w-4"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
// import BotBusyAnimation from "@/components/shared/BotBusyAnimation.vue";
import V2MessageBubble from "./V2MessageBubble.vue";
import type { V2MessageWithParsedContent } from "@/types/v2";
import ConversationLoading from "./ConversationLoading.vue";
import ConversationError from "./meta/ConversationError.vue";

defineProps<{
  isRecent?: boolean;
  messages: V2MessageWithParsedContent[];
  loading?: boolean;
  error?: boolean;
  conversationId: string;
  isWidget?: boolean;
}>();

const emit = defineEmits(["retry"]);
const currentLoadingTitle = ref("Analyzing input...");

const v2ResponseStore = useV2ResponseStore();
const lastElement = ref<HTMLElement | null>(null);
let intervalId: NodeJS.Timeout | null = null;
let count = 0;

const loadingTitles = [
  "Analyzing input...",
  "Curating data...",
  "Checking for accuracy...",
  "Generating response...",
  "Finalizing...",
];

const isProcessing = computed(() => {
  const isNew = v2ResponseStore.isNewConversation;
  if (
    isNew &&
    !v2ResponseStore.lastConversationId &&
    !!v2ResponseStore.processingConversations["new"]
  ) {
    return true;
  }
  return !!(
    v2ResponseStore.lastConversationId &&
    v2ResponseStore.processingConversations[v2ResponseStore.lastConversationId]
  );
});

function scrollToEnd() {
  if (!lastElement.value) return;
  lastElement.value.scrollIntoView({ behavior: "auto" });
}

function startTextTimer() {
  intervalId = setInterval(() => {
    currentLoadingTitle.value = loadingTitles[count % loadingTitles.length];
    count++;
  }, 5 * SECOND);
}

function handleConfirmAction(value: string) {
  sendInstantV2Message({ text: value });
}

watchEffect(() => {
  if (isProcessing && !intervalId) {
    startTextTimer();
  }
  if (intervalId && !isProcessing) {
    count = 0;
    currentLoadingTitle.value = loadingTitles[0];
    clearInterval(intervalId);
    intervalId = null;
  }
});

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
});
</script>
